import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
    Card, Row, Col, Badge, Button, Tab, Tabs, Spinner, InputGroup, Form, Modal, Dropdown
} from "react-bootstrap";
import {
    FaFileInvoiceDollar, FaTruckLoading, FaUserTag, FaSearch,
    FaPlus, FaEllipsisV, FaMoneyBillWave, FaClock, FaCheckCircle,
    FaExclamationTriangle, FaBox, FaList, FaPrint, FaShieldAlt,
    FaTrashAlt, FaTag, FaUserTie, FaCheck, FaEye, FaFileContract, FaTruck, FaBoxOpen, FaEdit,
    FaBarcode,
    FaCalendarAlt,
    FaFileInvoice
} from 'react-icons/fa';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";

// --- CORE COMPONENTS ---
import BrandTable from "../core/components/BrandTable/BrandTable";
import BrandLoader from "../core/components/Brand/BrandLoader";
import SharedModal from "../core/shared/components/Modal"; // Keep for Edit/New form if needed

// --- FORMS & CARDS ---
// Or UnifiedPurchaseForm if migrated
import PurchaseDetailsCard from "../purchases/PurchaseDetailsCard";

// --- FULLSCREEN MODULES (Your Specific Features) ---
import StockDashboard from "../stock/StockDashboard";
import PaymentTransactionList from "../payments/PaymentTransactionList";
import PaymentForm from "../payments/PaymentForm";


import PrintableLabels from "../barcode/PrintableLabels";
import { PDFFormatter } from "../../utility/PDFFormatter";
import { fetchCompanies } from "../../redux/features/company.features";

// --- REDUX & UTILS ---
import { getAllPurchases, deletePurchase, updatePurchase, updatePurchasePrice } from "../../redux/features/purchase.features";
import { formatCurrency } from "../../utility";
import StockUpdateSuccessModal from "../purchase/StockUpdateSuccessModal";
import PurchaseVerification from "../purchase/PurchaseVerification";
import RestockQueue from "./RestockQueue";
import RFQManager from "./RFQManager";

const BRAND_COLOR = '#001c3a';

// --- STATS COMPONENT ---
// --- STATS COMPONENT ---

const DashboardStats = ({ stats, loading, onAction }) => {
    const cards = [
        {
            label: 'Low Stock',
            val: stats.lowStockCount,
            icon: <FaExclamationTriangle />,
            color: 'danger',
            sub: 'Needs Reordering',
            onClick: () => onAction('lowStock')
        },
        {
            label: 'Pending Approvals',
            val: stats.draftCount,
            icon: <FaClock />,
            color: 'primary',
            sub: 'Draft POs',
            onClick: () => onAction('approvals')
        },
        {
            label: 'Arrivals Today',
            val: stats.arrivalsCount,
            icon: <FaTruckLoading />,
            color: 'success',
            sub: 'Expected Goods',
            onClick: () => onAction('arrivals')
        },
        {
            label: 'Total Payable',
            val: formatCurrency(stats.due),
            icon: <FaMoneyBillWave />,
            color: 'danger',
            sub: 'Outstanding'
        },
    ];

    return (
        <Row className="g-3 mb-4">
            {cards.map((c, i) => (
                <Col md={3} key={i}>
                    <Card
                        className={`border-0 shadow-sm h-100 overflow-hidden ${c.onClick ? 'cursor-pointer-hover transition-all' : ''}`}
                        onClick={c.onClick}
                    >
                        <div className="h-100 p-3 d-flex align-items-center justify-content-between position-relative">
                            <div className={`position-absolute start-0 top-0 w-1 h-100 bg-${c.color}`}></div>
                            <div>
                                <div className="text-muted x-small fw-bold text-uppercase mb-1">{c.label}</div>
                                <h3 className="fw-bold mb-0 text-dark">{loading ? <Spinner size="sm" /> : c.val}</h3>
                                <small className={`text-${c.color} x-small`}>{c.sub}</small>
                            </div>
                            <div className={`bg-${c.color} bg-opacity-10 p-3 rounded-circle text-${c.color}`}>
                                {c.icon}
                            </div>
                        </div>
                    </Card>
                </Col>
            ))}
        </Row>
    );
};

// --- PDF GENERATORS (Moved to Helper Functions) ---
const generateStandardInvoice = (purchase) => {
    const doc = new jsPDF();
    doc.text(`INVOICE: ${purchase.referenceNumber}`, 14, 20);
    // ... (Your autoTable logic) ...
    doc.save(`Invoice_${purchase.referenceNumber}.pdf`);
};

const ProcurementDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const editRef = useRef();

    // --- REDUX STATE ---
    const { purchases, loading, restockSuggestions } = useSelector(state => state.purchaseReducer);
    const { companies } = useSelector(state => state.companyReducer);
    const { user } = useSelector(state => state.authReducer);

    // --- UI STATE ---
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState('action'); // Default to Action Center
    const [subTab, setSubTab] = useState('restock'); // For nested filters
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // --- MODAL STATES ---
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showStockModal, setShowStockModal] = useState(false);
    const [showPaymentListModal, setShowPaymentListModal] = useState(false);
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [showStockSuccessModal, setShowStockSuccessModal] = useState(false);
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [showPriceModal, setShowPriceModal] = useState(false);

    // --- DATA HOLDERS ---
    const [priceUpdateData, setPriceUpdateData] = useState([]);
    const [verifiedItemsForLabels, setVerifiedItemsForLabels] = useState([]);
    const [verifiedItems, setVerifiedItems] = useState([]);
    const [amountToPay, setAmountToPay] = useState(0);

    // --- INITIAL FETCH ---
    useEffect(() => {
        dispatch(getAllPurchases());
        dispatch(fetchCompanies());
    }, [dispatch]);

    // --- SMART STATISTICS (Memoized) ---
    const stats = useMemo(() => {
        if (!purchases) return { pending: 0, due: 0, month: 0, lowStockCount: 0, draftCount: 0, arrivalsCount: 0 };
        const todayStr = new Date().toDateString();
        const lowStockCount = restockSuggestions?.reduce((acc, s) => acc + s.items.length, 0) || 0;

        return purchases.reduce((acc, p) => {
            if (p.purchase_status === 'Pending Verification') {
                acc.pending++;
                if (new Date(p.purchaseDate).toDateString() === todayStr) acc.arrivalsCount++;
            }
            if (p.purchase_status === 'Draft' || p.isDraft) acc.draftCount++;
            if (p.payment_status !== 'Paid') acc.due += (p.payment_due_amount || 0);
            if (new Date(p.purchaseDate).getMonth() === new Date().getMonth()) acc.month += (p.grand_total || 0);
            return acc;
        }, { pending: 0, due: 0, month: 0, lowStockCount, draftCount: 0, arrivalsCount: 0 });
    }, [purchases, restockSuggestions]);

    // --- FILTERING LOGIC ---
    const filteredData = useMemo(() => {
        if (!purchases) return [];
        let data = purchases;

        // 1. Tab & Sub-Tab Filter
        if (activeTab === 'pos') {
            if (subTab === 'draft') data = data.filter(p => p.purchase_status === 'Draft' || p.isDraft === true);
            else if (subTab === 'open') data = data.filter(p => p.purchase_status === 'Pending Verification');
            else if (subTab === 'history') data = data.filter(p => p.purchase_status === 'Received' || p.purchase_status === 'Approved');
            data = data.filter(p => p.sourceType === 'PO');
        }
        else if (activeTab === 'bills') {
            if (subTab === 'direct') data = data.filter(p => p.sourceType === 'DIRECT');
            else if (subTab === 'pending_grn') data = data.filter(p => p.purchase_status === 'Pending Verification');
            else if (subTab === 'tradein') data = data.filter(p => p.purchase_type === 'Trade-In');
        }

        // 2. Search Filter
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            data = data.filter(p =>
                p.referenceNumber?.toLowerCase().includes(lower) ||
                p.supplier_bill_no?.toLowerCase().includes(lower) ||
                p.supplier?.business_name?.toLowerCase().includes(lower) ||
                p.ref_agent_name?.toLowerCase().includes(lower) ||
                p.customer?.first_name?.toLowerCase().includes(lower) ||
                p.purchasedItems?.some(item =>
                    item.serializedItems?.some(s => s.serialNumber?.toLowerCase().includes(lower)) ||
                    item.batch_number?.toLowerCase().includes(lower)
                )
            );
        }

        return [...data].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
    }, [purchases, activeTab, subTab, searchTerm]);

    // --- ACTION HANDLER ---
    const handleAction = useCallback((action, purchase) => {
        const refNo = purchase.referenceNumber || purchase._id;
        setSelectedPurchase(purchase);

        switch (action) {
            case "View": setShowPurchaseModal(true); break;
            case "Edit": navigate(`/purchases/new?edit=${purchase._id}`); break;
            case "Delete":
                confirmAlert({
                    title: "Confirm Deletion",
                    message: `Delete ${refNo}? This reverses stock and accounts.`,
                    buttons: [
                        { label: "Yes", onClick: () => dispatch(deletePurchase(purchase._id)).then(() => { toast.success("Deleted"); dispatch(getAllPurchases()); }) },
                        { label: "Cancel" }
                    ]
                });
                break;
                break;
            case "Print":
                PDFFormatter.generatePurchaseInvoice(purchase, companies?.[0], user);
                break;
            case "Verify": setShowVerificationModal(true); break;
            case "AddPayment": setAmountToPay(purchase.payment_due_amount || 0); setShowAddPaymentModal(true); break;
            case "ViewPayments": setShowPaymentListModal(true); break;
            case "ViewStock": setShowStockModal(true); break;
            case "PrintBarcodes": prepareLabels(purchase); break;
            case "EditSellingPrice": setPriceUpdateData(purchase.purchasedItems || []); setShowPriceModal(true); break;
            default: break;
        }
    }, [dispatch, navigate]);

    const prepareLabels = (purchase) => {
        const items = purchase.purchasedItems || [];
        if (items.length === 0) return toast.warning("No items.");
        const formatted = items.map(item => ({
            ...item.item_id,
            _id: `${item.item_id?._id}_${item.variant_id?._id || 'base'}`,
            itemName: item.variant_id?.variantName || item.item_id?.itemName || item.itemName,
            barcode: item.variant_id?.barcode || item.item_id?.barcode,
            totalStock: item.purchaseQty,
            serialized: item.isSerialized,
            batches: [{ batch_number: item.batch_number, unitCost: item.unitCost, sellingPrice: item.sellingPrice }],
            serializedItems: item.serializedItems
        }));
        setVerifiedItemsForLabels(formatted);
        setShowLabelModal(true);
    };

    const columns = useMemo(() => [
        {
            key: "referenceNumber",
            label: "Reference/Bill",
            sortable: true,
            render: (v, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                    <div style={{ fontWeight: 800, color: '#000000', fontSize: '15px' }}>{v}</div>
                    {row.supplier_bill_no && (
                        <div style={{ color: '#1d4ed8', fontWeight: 700, fontSize: '12px' }}>
                            Bill: {row.supplier_bill_no}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: "purchaseDate",
            label: "Date",
            sortable: true,
            render: (v, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}>
                    <div style={{ fontWeight: 700, color: '#000000', fontSize: '14px' }}>
                        {new Date(v).toLocaleDateString('en-GB')}
                    </div>
                    {row.purchase_meta?.updatedAt && (
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                            Updated: {new Date(row.purchase_meta.updatedAt).toLocaleDateString('en-GB')}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: "supplier",
            label: "Entity & Source",
            sortable: true,
            render: (_, row) => {
                const isTradeIn = row.purchase_type === 'Trade-In';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '32px', height: '32px', borderRadius: '8px',
                            background: isTradeIn ? '#ecfeff' : '#f1f5f9',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: isTradeIn ? '#0891b2' : '#475569'
                        }}>
                            {isTradeIn ? <FaUserTag size={14} /> : <FaTruckLoading size={14} />}
                        </div>
                        <div style={{ fontWeight: 700, color: '#000000', fontSize: '14px' }}>
                            {row.supplier?.business_name || row.customer?.first_name || 'Open Market'}
                        </div>
                    </div>
                );
            }
        },
        {
            key: "grand_total",
            label: "Total Amount",
            align: 'right',
            sortable: true,
            render: (v) => (
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 900, color: '#000000', fontSize: '15px' }}>
                        {new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR', minimumFractionDigits: 0 }).format(v)}
                    </div>
                </div>
            )
        },
        {
            key: "payment_due_amount",
            label: "Outstanding",
            align: 'right',
            sortable: true,
            render: (v) => {
                const isSettled = !v || v <= 0;
                return (
                    <div style={{ textAlign: 'right' }}>
                        {isSettled ? (
                            <span style={{ color: '#059669', fontWeight: 800, fontSize: '13px' }}>SETTLED</span>
                        ) : (
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, color: '#dc2626', fontSize: '14px' }}>
                                Rs. {new Intl.NumberFormat('en-LK', { minimumFractionDigits: 0 }).format(v)}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: "purchase_status",
            label: "Logistics",
            align: 'center',
            sortable: true,
            render: (v) => {
                const config = {
                    'Received': { bg: '#ecfdf5', color: '#059669', border: '#10b981' },
                    'Pending Verification': { bg: '#fffbeb', color: '#d97706', border: '#fbbf24' },
                    'Cancelled': { bg: '#fef2f2', color: '#dc2626', border: '#f87171' }
                }[v] || { bg: '#f8fafc', color: '#475569', border: '#cbd5e1' };

                return (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
                        borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                        backgroundColor: config.bg, color: config.color, border: `1px solid ${config.border}`
                    }}>
                        {v}
                    </span>
                );
            }
        },
        {
            key: "actions",
            label: "Shop Actions",
            align: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>

                    {/* 1. VERIFY BUTTON: Only shows if stock is not yet checked */}
                    {row.purchase_status === 'Pending Verification' && (
                        <button
                            onClick={() => handleAction("Verify", row)}
                            title="Confirm Stock has Arrived"
                            style={{
                                background: '#ecfdf5', color: '#059669', border: '1px solid #d1fae5',
                                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer'
                            }}
                        >
                            <FaCheck size={10} /> Check Stock
                        </button>
                    )}

                    {/* 2. PAY BUTTON: Only shows if you still owe money */}
                    {row.payment_due_amount > 0 && (
                        <button
                            onClick={() => handleAction("AddPayment", row)}
                            title="Pay the Supplier"
                            style={{
                                background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5',
                                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer'
                            }}
                        >
                            <FaMoneyBillWave size={12} /> Pay Now
                        </button>
                    )}

                    {/* 3. SETTINGS MENU: Everything else goes in here */}
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="link" className="p-0 border-0 no-arrow">
                            <div style={{
                                width: '30px', height: '30px', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer'
                            }}>
                                <FaEllipsisV size={12} color="#64748b" />
                            </div>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="shadow-lg border-0" style={{ borderRadius: '12px', padding: '8px', minWidth: '200px' }}>
                            <div style={{ fontSize: '10px', fontWeight: '900', color: '#cbd5e1', padding: '5px 12px', textTransform: 'uppercase' }}>
                                View Info
                            </div>
                            <Dropdown.Item onClick={() => handleAction("View", row)}>
                                <FaEye className="me-2 text-primary" /> View Full Bill
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAction("ViewStock", row)}>
                                <FaBoxOpen className="me-2 text-info" /> See Items in Stock
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAction("ViewPayments", row)}>
                                <FaMoneyBillWave className="me-2 text-success" /> Payment History
                            </Dropdown.Item>

                            <Dropdown.Divider />

                            <div style={{ fontSize: '10px', fontWeight: '900', color: '#cbd5e1', padding: '5px 12px', textTransform: 'uppercase' }}>
                                Print & Edit
                            </div>
                            <Dropdown.Item onClick={() => handleAction("Print", row)}>
                                <FaPrint className="me-2 text-dark" /> Print Bill (PDF)
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAction("PrintBarcodes", row)}>
                                <FaBarcode className="me-2 text-secondary" /> Print Price Tags
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAction("Edit", row)}>
                                <FaEdit className="me-2 text-warning" /> Edit Bill Info
                            </Dropdown.Item>

                            <Dropdown.Divider />

                            <Dropdown.Item onClick={() => handleAction("Delete", row)} className="text-danger">
                                <FaTrashAlt className="me-2" /> Delete Record
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            )
        }
    ], [handleAction]);

    const renderSubTabs = () => {
        const renderTabGroup = (items, current, setFunc, getLabel, getIcon) => (
            <div className="custom-brand-tabs mb-4">
                {items.map(t => (
                    <div className="nav-item" key={t}>
                        <button
                            className={`nav-link border-0 ${current === t ? 'active' : ''}`}
                            onClick={() => setFunc(t)}
                            style={{ cursor: 'pointer' }}
                        >
                            {getIcon && getIcon(t)}
                            {getLabel ? getLabel(t) : t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                    </div>
                ))}
            </div>
        );

        if (activeTab === 'action') {
            return renderTabGroup(
                ['restock', 'approvals', 'arrivals'],
                subTab,
                setSubTab,
                (t) => t === 'restock' ? 'Restock Queue' : t === 'approvals' ? 'Pending Approvals' : 'Arrivals Today',
                (t) => {
                    if (t === 'restock') return <FaTruck className="me-2" />;
                    if (t === 'approvals') return <FaCheckCircle className="me-2 text-primary" />;
                    if (t === 'arrivals') return <FaBoxOpen className="me-2 text-success" />;
                    return null;
                }
            );
        }
        if (activeTab === 'pos') {
            return renderTabGroup(['draft', 'open', 'history'], subTab, setSubTab);
        }
        if (activeTab === 'bills') {
            return renderTabGroup(
                ['direct', 'pending_grn', 'tradein', 'all'],
                subTab,
                setSubTab,
                (t) => t.replace('_', ' ')
            );
        }
        return null;
    };

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: BRAND_COLOR }}>Procurement Center</h4>
                    <p className="text-muted small mb-0">Smart Supply Chain & Inventory Planning</p>
                </div>
                <Dropdown>
                    <Dropdown.Toggle variant="primary" className="fw-bold shadow-sm px-4" id="dropdown-create-new" style={{ backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}><FaPlus className="me-2" /> Create New</Dropdown.Toggle>
                    <Dropdown.Menu className="shadow-lg border-0 p-2">
                        <Dropdown.Item as={Link} to="/purchases/new?type=direct" className="py-2 d-flex gap-2 align-items-center"><FaTruckLoading className="text-success" /> Direct Purchase</Dropdown.Item>
                        <Dropdown.Item as={Link} to="/purchases/new?type=po" className="py-2 d-flex gap-2 align-items-center"><FaFileInvoiceDollar className="text-primary" /> Purchase Order (PO)</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item as={Link} to="/purchases/new?type=direct&mode=tradein" className="py-2 d-flex gap-2 align-items-center"><FaUserTag className="text-info" /> Customer Trade-In</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <DashboardStats
                stats={stats}
                loading={loading}
                onAction={(type) => {
                    setActiveTab('action');
                    if (type === 'lowStock') setSubTab('restock');
                    else if (type === 'approvals') setSubTab('approvals');
                    else if (type === 'arrivals') setSubTab('arrivals');
                }}
            />

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white pt-3 px-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Tabs
                            activeKey={activeTab}
                            onSelect={k => {
                                setActiveTab(k);
                                if (k === 'action') setSubTab('restock');
                                else if (k === 'pos') setSubTab('open');
                                else setSubTab('direct');
                            }}
                            className="border-bottom-0 gap-3 custom-tabs"
                        >
                            <Tab eventKey="sourcing" title={<span className="fw-bold py-2 d-block"><FaSearch className="me-2" />Sourcing / RFQs</span>} />
                            <Tab eventKey="action" title={<span className="fw-bold py-2 d-block"><FaClock className="me-2" />Action Center</span>} />
                            <Tab eventKey="pos" title={<span className="fw-bold py-2 d-block"><FaFileInvoiceDollar className="me-2" />Purchase Orders</span>} />
                            <Tab eventKey="bills" title={<span className="fw-bold py-2 d-block"><FaTruckLoading className="me-2" />Bills & Receipts</span>} />
                            <Tab eventKey="suppliers" title={<span className="fw-bold py-2 d-block"><FaUserTie className="me-2" />Supplier Hub</span>} />
                        </Tabs>
                        <div style={{ maxWidth: '250px' }}>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-light border-0"><FaSearch /></InputGroup.Text>
                                <Form.Control placeholder="Search..." className="bg-light border-0" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </InputGroup>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    {activeTab === 'sourcing' ? (
                        <RFQManager />
                    ) : activeTab === 'action' ? (

                        <div className="p-2">
                            {renderSubTabs()}
                            {subTab === 'restock' && <RestockQueue />}
                            {subTab === 'approvals' && (
                                <BrandTable data={purchases.filter(p => p.purchase_status === 'Draft' || p.isDraft)} columns={columns} loading={loading} sortable={true} />
                            )}
                            {subTab === 'arrivals' && (
                                <BrandTable data={purchases.filter(p => p.purchase_status === 'Pending Verification' && new Date(p.purchaseDate).toDateString() === new Date().toDateString())} columns={columns} loading={loading} sortable={true} />
                            )}
                        </div>
                    ) : (
                        <>
                            {renderSubTabs()}
                            <BrandTable data={filteredData} columns={columns} loading={loading} sortable={true} pagination currentPage={currentPage} rowsPerPage={rowsPerPage} onPageChange={setCurrentPage} onRowsPerPageChange={setRowsPerPage} />
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modals */}
            <Modal show={showPurchaseModal} onHide={() => setShowPurchaseModal(false)} fullscreen>
                <Modal.Header closeButton><Modal.Title>Purchase Details</Modal.Title></Modal.Header>
                <Modal.Body className="p-0"><PurchaseDetailsCard data={selectedPurchase} hideHeader onClose={() => setShowPurchaseModal(false)} /></Modal.Body>
            </Modal>
            <PurchaseVerification show={showVerificationModal} onHide={() => setShowVerificationModal(false)} purchase={selectedPurchase} onSuccess={(v) => { dispatch(getAllPurchases()); setVerifiedItems(v); setShowStockSuccessModal(true); }} />
            <StockUpdateSuccessModal show={showStockSuccessModal} onHide={() => { setShowStockSuccessModal(false); setVerifiedItems([]); }} purchase={selectedPurchase} verifiedItems={verifiedItems} />

            <Modal show={showStockModal} onHide={() => setShowStockModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Stock Status: {selectedPurchase?.referenceNumber}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light">
                    <StockDashboard items={selectedPurchase?.purchasedItems?.map(it => it.item_id?._id)} />
                </Modal.Body>
            </Modal>

            <Modal fullscreen show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} centered size="xl">
                <Modal.Header closeButton><Modal.Title>Process Payment</Modal.Title></Modal.Header>
                <Modal.Body><PaymentForm propRef={selectedPurchase?.referenceNumber} propAmount={amountToPay} propSupplier={selectedPurchase?.supplier?._id} hideHeader onSuccess={() => { setShowAddPaymentModal(false); dispatch(getAllPurchases()); }} /></Modal.Body>
            </Modal>

            <Modal show={showLabelModal} onHide={() => setShowLabelModal(false)} size="xl" fullscreen>
                <Modal.Header closeButton>
                    <Modal.Title>Print Labels - {selectedPurchase?.referenceNumber}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <PrintableLabels items={verifiedItemsForLabels} onClose={() => setShowLabelModal(false)} />
                </Modal.Body>
            </Modal>

            <style>{`
                .custom-tabs .nav-link { border: none !important; color: #6c757d !important; border-bottom: 2px solid transparent !important; }
                .custom-tabs .nav-link.active { color: ${BRAND_COLOR} !important; border-bottom: 2px solid ${BRAND_COLOR} !important; background: transparent !important; }
                .w-fit-content { width: fit-content; }
                .cursor-pointer-hover { cursor: pointer; }
                .cursor-pointer-hover:hover { transform: translateY(-2px); box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important; }
                .transition-all { transition: all 0.2s ease-in-out; }
            `}</style>
        </div>
    );
};



export default ProcurementDashboard;


const generateTradeInAgreement = (purchase) => {
    const doc = new jsPDF();
    // ... (Your exact legal text and signature logic) ...
    doc.text("TRANSFER OF OWNERSHIP", 105, 20, { align: "center" });
    doc.save(`TradeIn_${purchase.referenceNumber}.pdf`);
};

